<template>
  <page-section :description="$t('RetentionAuditLog_Subtitle')">
    <rest-list :payload="fetchEndpoint" :per-page="50" ref="restList">
      <rest-list-column-date
        prop="create_time"
        :title="$t('RetentionAuditLog_Field_Date')"
        :width="200"
        :sortable="true"
        :filterable="true"
      />

      <rest-list-column-enum
        prop="action"
        :title="$t('RetentionAuditLog_Field_Action')"
        :enum="RetentionSchemaAction"
        :sortable="false"
        :filterable="true"
        :width="150"
      />

      <rest-list-column-enum
        prop="section"
        :title="$t('RetentionAuditLog_Field_Section')"
        :enum="RetentionSchemaSection"
        :sortable="false"
        :filterable="true"
        :width="200"
      />

      <rest-list-column
        prop="updated_by"
        :title="$t('RetentionAuditLog_Field_UpdatedBy')"
        :filterable="false"
        :sortable="false"
        :width="150"
        :filter-input="false"
      />

      <rest-list-column
        prop="changes"
        :title="$t('RetentionAuditLog_Field_Summary')"
        :sortable="false"
        :filterable="false"
      >
        <template v-slot="scope">
          <div class="summary" v-if="scope.row.changes">
            {{ formatSummary(scope.row) }}
          </div>
          <template v-else>{{
            $t('RetentionAuditLog_Summary_NoChange')
          }}</template>
        </template>
      </rest-list-column>
    </rest-list>
  </page-section>
</template>

<script>
import PageSection from '@/common/components/page/PageSection'
import RestList from '@/common/components/rest/RestList'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import { projectEndpoint } from '@/core/helpers'
import {
  RetentionSchemaAction,
  RetentionSchemaSection
} from '@/modules/settings/enum/RetentionAuditLogEnums'
import {
  BOOLEAN_FIELDS,
  FIELD_LABEL_KEYS,
  SECTION_LABEL_KEYS,
  FIELD_UNIT_KEYS
} from '@/modules/settings/enum/RetentionAuditLogLabels'

export default {
  name: 'RetentionAuditLogTab',

  components: {
    PageSection,
    RestListColumnDate,
    RestListColumn,
    RestListColumnEnum,
    RestList
  },

  data() {
    return {
      RetentionSchemaAction,
      RetentionSchemaSection
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'retention-audit-log'
      })
    }
  },

  methods: {
    _label(field) {
      return FIELD_LABEL_KEYS[field] ? this.$t(FIELD_LABEL_KEYS[field]) : field
    },

    _unit(field) {
      return FIELD_UNIT_KEYS[field] ? this.$t(FIELD_UNIT_KEYS[field]) : null
    },

    _isEmpty(v) {
      return v === null || v === undefined || v === '' || v === 'null'
    },

    _formatCreateField(field, value) {
      if (BOOLEAN_FIELDS.has(field) || this._isEmpty(value)) return null
      const label = this._label(field)
      const unit = this._unit(field)
      if (!unit) {
        return this.$t('RetentionAuditLog_Summary_FieldSetToNoUnit', {
          label,
          value
        })
      }
      return this.$t('RetentionAuditLog_Summary_FieldSetTo', {
        label,
        value,
        unit
      })
    },

    _formatUpdateField(field, oldVal, newVal) {
      const label = this._label(field)
      const unit = this._unit(field)
      if (BOOLEAN_FIELDS.has(field)) {
        return String(newVal) === '1'
          ? this.$t('RetentionAuditLog_Summary_Enabled', { label })
          : this.$t('RetentionAuditLog_Summary_Disabled', { label })
      }
      if (this._isEmpty(newVal)) {
        return this.$t('RetentionAuditLog_Summary_Disabled', { label })
      }
      if (this._isEmpty(oldVal)) {
        if (!unit) {
          return this.$t('RetentionAuditLog_Summary_Enabled', { label })
        }
        return this.$t('RetentionAuditLog_Summary_EnabledWith', {
          label,
          value: newVal,
          unit
        })
      }
      if (String(oldVal) === String(newVal)) return null
      if (!unit) {
        return this.$t('RetentionAuditLog_Summary_UpdatedFromNoUnit', {
          label,
          old: oldVal,
          new: newVal
        })
      }
      return this.$t('RetentionAuditLog_Summary_UpdatedFrom', {
        label,
        old: oldVal,
        new: newVal,
        unit
      })
    },

    formatSummary(row) {
      const { changes, action, section } = row
      if (!changes || typeof changes !== 'object') {
        return this.$t('RetentionAuditLog_Summary_NoChange')
      }

      const noChange = this.$t('RetentionAuditLog_Summary_NoChange')
      const andStr = this.$t('RetentionAuditLog_Summary_And')
      const isCreate = action === 0
      const parts = []

      for (const sectionKey of Object.keys(changes)) {
        const data = changes[sectionKey]
        if (!data || typeof data !== 'object') continue

        const newValues = data.new || {}
        const originalValues = data.original || {}

        if (isCreate) {
          const sectionLabel =
            SECTION_LABEL_KEYS[section] != null
              ? this.$t(SECTION_LABEL_KEYS[section])
              : sectionKey
          const fieldParts = Object.keys(newValues)
            .map(f => this._formatCreateField(f, newValues[f]))
            .filter(Boolean)
          if (fieldParts.length > 0) {
            parts.push(
              this.$t('RetentionAuditLog_Summary_CreatedWith', {
                section: sectionLabel,
                fields: fieldParts.join(andStr)
              })
            )
          } else {
            parts.push(
              this.$t('RetentionAuditLog_Summary_Created', {
                section: sectionLabel
              })
            )
          }
        } else {
          parts.push(
            ...Object.keys(newValues)
              .map(field =>
                this._formatUpdateField(
                  field,
                  originalValues[field],
                  newValues[field]
                )
              )
              .filter(Boolean)
          )
        }
      }

      return parts.length > 0 ? parts.join('\n') : noChange
    }
  }
}
</script>

<style scoped>
.summary {
  white-space: pre-line;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/components/RetentionAuditLogTab.vue